import React, {useMemo, useRef, useState} from "react";
import {useAuth} from "../../../providers/AuthProvider";
import {Modal} from "antd";
import Form from "../../../components/form/Form";
import {useProject} from "../../../providers/ClientProjectProvider";
import ProjectActivityForm from "./ProjectActivityForm";
import ProjectDeliverableForm from "./ProjectDeliverableForm";
import {useSearchParams} from "react-router-dom";
import Project from "../../../models/Project";

let ProjectScopeContext = React.createContext(null);

const ProjectScopeProvider = ({children, ...props}) => {

    const {
        project,
        isDisabled,
        isProjectUpdating,
        postUpdate
    } = useProject();
    const {auth} = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const closeEditScopeModal = () => {
        let urlParams = new URLSearchParams(searchParams);
        urlParams.delete('editScope');
        return setSearchParams(urlParams)
    }
    const openEditScopeModal = (type) => {
        let urlParams = new URLSearchParams(searchParams);
        urlParams.set('editScope', type);
        return setSearchParams(urlParams)
    }


    const [scope, setscope] = useState(() => project.normalizeScopeForService());
    const [editingKey, setEditingKey] = useState(null);
    const formRef = useRef(null);

    const getRecordKey = (record) => {
        if (TB.isDefined(record.activity_index)) {
            return `phases[${record.phase_index - 1}].deliverables[${record.deliverable_index - 1}].activities[${record.activity_index - 1}].disciplines[${record.display_index - 1}]`
        } else if (TB.isDefined(record.deliverable_index)) {
            return `phases[${record.phase_index - 1}].deliverables[${record.deliverable_index - 1}].activities[${record.display_index - 1}]`
        } else if (TB.isDefined(record.phase_index)) {
            return `phases[${record.phase_index - 1}].deliverables[${record.display_index - 1}]`
        }
        return `phases[${record.display_index - 1}]`
    }
    const editingType = useMemo(() => {
        if (!editingKey) return false;
        if (editingKey.includes('disciplines')) {
            return 'disciplines'
        } else if (editingKey.includes('activities')) {
            return 'activities'
        } else if (editingKey.includes('deliverables')) {
            return 'deliverables'
        }
        return `phases`
    }, [editingKey]);

    const isEditing = (record) => {
        return getRecordKey(record) === editingKey
    }
    const isValid = (record) => {
        if (!isEditing(record)) return true;

        switch (editingType) {
            case 'disciplines':
                return record.total_hours > 0
            case 'activities':
                return record.disciplines?.length > 0 && record.description?.length > 0;
            case 'deliverables':
                return record.name?.length > 0 && record.assumptions?.length > 0;
        }
        return true;
    }

    const editScopeRecord = async (record) => {
        let newEditKey = getRecordKey(record);
        let type = `phases`;
        if (newEditKey.includes('disciplines')) {
            type = 'disciplines'
        } else if (newEditKey.includes('activities')) {
            type = 'activities'
        } else if (newEditKey.includes('deliverables')) {
            type = 'deliverables'
        }
        await setEditingKey(getRecordKey(record));


        // formRef.current?.forceReset(normalizeScope(scope))
        return openEditScopeModal(type)
    };

    const onChange = async (model) => {
        let newScope = {...scope};

        for (let [key, value] of Object.entries(model)) {
            if (!key.endsWith('disciplines')) {
                newScope = TB.set(newScope, key, value)
            }
        }
        for (let [key, value] of Object.entries(model)) {
            if (key.endsWith('disciplines') && TB.isArray(value) && TB.isInteger(value[0])) {
                let previousDisciplines = TB.get(scope, key) || [];

                let disciplines = TB.filter(project.disciplines, discipline => (value || []).includes(discipline.id));

                disciplines = TB.map(disciplines, discipline => {
                    if (TB.find(previousDisciplines, ['id', discipline.id])) {
                        return TB.find(previousDisciplines, ['id', discipline.id])
                    }
                    return {
                        ...discipline,
                        total_hours: 0,
                        ownership_weight: 0
                    }
                })

                newScope = TB.set(newScope, key, disciplines)
            }
        }
        newScope = Project.make(newScope)

        let updatedScope = newScope.normalizeScopeForService();
        await setscope(updatedScope);
        return updatedScope;
    }

    const cancelEdit = async () => {
        await closeEditScopeModal();
        await TB.wait(400);
        await setEditingKey(null)
        await setscope(project.normalizeScopeForService())
    };

    const saveDataUpdate = async () => {

        let response = await postUpdate(scope)

        await closeEditScopeModal();
        await TB.wait(400);
        await setEditingKey(null);
        if (response) {
            TB.toast('Project Scope Updated')
            await setscope(response.normalizeScopeForService())
        }
    };

    const value = {
        scope,
        getRecordKey,
        isValid,
        isEditing,
        editScopeRecord,
        isDisabled: isDisabled || isProjectUpdating,
        editingType,
        onChange,
        cancelEdit,
        saveDataUpdate,
        editingKey, setEditingKey
    }

    return (
        <ProjectScopeContext.Provider value={value}>
            <Form
                // ref={formRef}
                model={scope}
                onChange={onChange}
                onSubmit={null}
                submitButton={false}
                minimal
                background="transparent"
                fields={[]}
                isDisabled={false}
            >
                {children}
            </Form>

            {!!editingKey && (
                <Modal
                    okText={TB.get(scope, editingKey)?.id ? 'Update' : 'Save'}
                    title={`Edit Project ${TB.singularize(TB.capitalize(editingType))}`}
                    className="project-scope-modal"
                    open={searchParams.has('editScope')}
                    closeable={false}
                    maskClosable={false}
                    style={{top: 20}}
                    destroyOnClose={true}
                    width={1000}
                    onOk={saveDataUpdate}
                    confirmLoading={isDisabled || isProjectUpdating}
                    onCancel={() => {
                        if (isDisabled || isProjectUpdating) {
                            return
                        }
                        return cancelEdit()
                    }}
                >
                    <div className="project-scope-form">

                            {searchParams.get('editScope') === 'deliverables' ? (
                                <ProjectDeliverableForm
                                    onChange={onChange}
                                    record={TB.get(scope, editingKey)}
                                    recordKey={editingKey}/>
                            ) : searchParams.get('editScope') === 'activities' ? (
                                <ProjectActivityForm
                                    onChange={onChange}
                                    record={TB.get(scope, editingKey)}
                                    recordKey={editingKey}/>
                            ) : (
                                <React.Fragment>

                                </React.Fragment>
                            )}

                    </div>

                </Modal>
            )}

        </ProjectScopeContext.Provider>
    );
}



const useProjectScope = () => {
    return React.useContext(ProjectScopeContext);
}

export default ProjectScopeProvider;

export {useProjectScope, ProjectScopeContext}